import { jsx as _jsx } from "react/jsx-runtime";
// src/plugins/builder/templates/thank-you-positive/generator.tsx
// UI-only: posts a literal template string (no AI) as a thank-you to 5★ reviews.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { SimplePage } from '../../runtime/SimplePage';
function render(tpl, name) {
    return tpl.replaceAll('{name}', name || 'there');
}
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return (_jsx(SimplePage, { title: ctx.instanceName, subtitle: "Post a one-click thank-you to every 5-star review.", source: "reviews", filterReview: (r) => (r.rating ?? 0) === 5 && !r.response_body, actionLabel: "Post thank-you", emptyTitle: "No new 5\u2605 reviews to thank", emptyBody: "When a new 5-star review arrives, you can thank them here in one click.", onAction: async (item, h) => {
            const r = item;
            await h.reply(r.id, render(cfg.template, r.author_name));
            h.toast.success('Thank-you posted.');
        } }));
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Posts a fixed-template thank-you to every 5★ review.`,
        category: 'automation', icon: 'Heart',
        permissions: ['read:reviews', 'write:reviews', 'ui:toast'],
        entry: 'ui',
        config: meta.configSchema,
        defaultConfig: { template: cfg.template },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useState } from 'react';
import { useReviews, useReplyToReview, useToast } from '../sdk';
import { Button } from '../../ui/Button';

const TEMPLATE = ${JSON.stringify(cfg.template)};
function render(name: string | null) { return TEMPLATE.replaceAll('{name}', name || 'there'); }

export default function ${cls(params.instanceId)}() {
  const { reviews, refresh } = useReviews({ limit: 50 });
  const reply = useReplyToReview();
  const toast = useToast();
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const rows = (reviews ?? []).filter(r => (r.rating ?? 0) === 5 && !r.response_body);
  async function post(id: string, name: string | null) {
    setBusy(b => ({ ...b, [id]: true }));
    try { await reply(id, render(name)); toast.success('Thank-you posted.'); await refresh(); }
    catch (e: any) { toast.error(e?.message ?? 'Could not post.'); }
    finally { setBusy(b => ({ ...b, [id]: false })); }
  }
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <ul className="mt-6 space-y-3">
        {rows.map(r => (<li key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-slate-200"><p className="text-sm font-medium">{r.author_name ?? 'Anonymous'} · 5★</p><p className="mt-1 text-sm text-slate-600">{r.body}</p><div className="mt-3"><Button size="sm" disabled={!!busy[r.id]} onClick={() => post(r.id, r.author_name)}>{busy[r.id] ? 'Working…' : 'Post thank-you'}</Button></div></li>))}
      </ul>
    </main>
  );
}
`;
    return assemble({ manifest, pluginTsx, hooksTs: null,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `One-click thank-yous for 5★ reviews using a fixed template.`,
            configSummary: [['template', cfg.template]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: () => 'Simple thank-yous',
    describe: (cfg) => `When you open the page, post "${cfg.template}" to any new 5★ review in one click.`,
    generate, runtimeUi: RuntimeUi,
    clickCount: meta.clickCount,
};
export default template;
